import { Page, Reveal, MaskedLine } from "@/components/Page";
import { Marquee } from "@/components/Marquee";
import { useLang } from "@/i18n";
import { useContent } from "@/content";
import { useSeo } from "@/hooks/useSeo";
import { fileUrl } from "@/api";

const localized = (value, lang, fallback = "") => {
  if (value == null) return fallback;

  if (typeof value === "string" || typeof value === "number") {
    return value;
  }

  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === "object") {
    return value[lang] ?? value.tr ?? value.en ?? fallback;
  }

  return fallback;
};

export default function Studio() {
  const { lang, t } = useLang();
  const cms = useContent();

  const seo = cms.site?.seo?.studio || {};

  useSeo({
    title: localized(seo.title, lang, ""),
    description: localized(seo.description, lang, ""),
  });

  const rawStatement = localized(
    cms.site?.studio?.statement,
    lang,
    t.studio.statement
  );

  const statement = Array.isArray(rawStatement)
    ? rawStatement
    : [rawStatement].filter(Boolean);

  const about = localized(
    cms.site?.studio?.about,
    lang,
    t.studio.about
  );

  const rawApproach = localized(
    cms.site?.studio?.approach,
    lang,
    t.studio.approach
  );

  const approach = Array.isArray(rawApproach)
    ? rawApproach
    : t.studio.approach;

  const people = Array.isArray(cms.team)
    ? cms.team
    : [];

  return (
    <Page testid="studio-page" className="bg-bone">
      <section className="px-5 sm:px-10 pt-36 sm:pt-48 pb-20 sm:pb-28">
        <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-clay">
          <MaskedLine delay={0.2}>
            {t.studio.label}
          </MaskedLine>
        </span>

        <h1 className="mt-6 max-w-4xl text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight leading-[1.1]">
          {statement.map((line, i) => (
            <MaskedLine
              key={i}
              delay={0.35 + i * 0.15}
            >
              {localized(line, lang, "")}
            </MaskedLine>
          ))}
        </h1>
      </section>

      <Marquee />

      <section
        data-testid="studio-about-section"
        className="px-5 sm:px-10 py-24 sm:py-36 grid grid-cols-1 md:grid-cols-12 gap-10"
      >
        <Reveal className="md:col-span-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-clay">
            {t.studio.aboutLabel}
          </span>
        </Reveal>

        <Reveal className="md:col-span-8 md:col-start-5">
          <p className="text-base sm:text-lg lg:text-xl font-light leading-relaxed text-ink/90 max-w-2xl">
            {about}
          </p>
        </Reveal>
      </section>

      <section
        data-testid="studio-approach-section"
        className="bg-ink text-white px-5 sm:px-10 py-24 sm:py-36"
      >
        <Reveal>
          <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-white/50">
            {t.studio.approachLabel}
          </span>
        </Reveal>

        <div className="mt-12">
          {approach.map((a, i) => {
            const number =
              localized(a?.n, lang, "") ||
              String(i + 1).padStart(2, "0");

            const title = localized(
              a?.title,
              lang,
              ""
            );

            const text = localized(
              a?.text,
              lang,
              ""
            );

            return (
              <Reveal
                key={`${number}-${i}`}
                delay={i * 0.04}
              >
                <div
                  data-testid={`approach-item-${number}`}
                  className="grid grid-cols-12 gap-4 border-t border-white/15 py-10 sm:py-14"
                >
                  <span className="col-span-2 sm:col-span-1 font-mono text-xs text-white/50 pt-1">
                    {number}
                  </span>

                  <h3 className="col-span-10 sm:col-span-4 text-lg sm:text-xl font-light uppercase tracking-tight">
                    {title}
                  </h3>

                  <p className="col-span-10 col-start-3 sm:col-span-6 sm:col-start-7 text-sm sm:text-base font-light leading-relaxed text-white/70">
                    {text}
                  </p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section
        data-testid="studio-people-section"
        className="px-5 sm:px-10 py-24 sm:py-36"
      >
        <Reveal>
          <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-clay">
            {t.studio.peopleLabel}
          </span>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 sm:gap-16 max-w-5xl">
          {people.map((p, i) => {
            const name = localized(
              p?.name,
              lang,
              ""
            );

            const title = localized(
              p?.title,
              lang,
              ""
            );

            const bio = localized(
              p?.bio,
              lang,
              t.studio.bio
            );

            return (
              <Reveal
                key={p?.id || `${name}-${i}`}
                delay={i * 0.08}
              >
                <div data-testid={`person-card-${i}`}>
                  {p?.portrait ? (
                    <img
                      src={fileUrl(p.portrait)}
                      alt={name}
                      loading="lazy"
                      className="aspect-[3/4] w-full object-cover"
                    />
                  ) : (
                    <div className="aspect-[3/4] bg-stone flex items-center justify-center">
                      <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-clay">
                        {t.studio.portrait}
                      </span>
                    </div>
                  )}

                  <h3 className="mt-5 text-lg sm:text-xl font-light uppercase tracking-tight">
                    {name}
                  </h3>

                  {title && (
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-clay">
                      {title}
                    </p>
                  )}

                  <p className="mt-2 text-sm font-light leading-relaxed text-ink/60">
                    {bio}
                  </p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>
    </Page>
  );
}
